import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import type { ParsedUrlQuery } from 'querystring'
import { getTheatricalRelease } from 'src/services/Theatrical'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { NextPageWithLayout } from '@/layouts'
import { TicketsLayout } from '@/layouts/TicketLayout'
import { getProjectGuildScore } from '@/services/ProjectsService'
import { TheatricalReleaseObject, TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'

const UPSERT_THEATRICAL_USER_PREFERENCES = gql`
  mutation UpsertTheatricalUserPreferences($input: UserPreferenceInput!) {
    upsertTheatricalUserPreferences(input: $input) {
      id
      preferredVenues {
        id
        name
        providerId
      }
    }
  }
`

interface TicketsPageProps {
  theatricalRelease: TheatricalReleaseObject
  theatricalSlug: string
  showOverlayVideoExperiment?: boolean
  isDecrementingDiscount?: boolean
  projectGuildScore?: number
  experiments?: ServerExperiments
}

export const TicketsHypebotPage: NextPageWithLayout<TicketsPageProps> = ({
  theatricalRelease,
  theatricalSlug,
  projectGuildScore,
  experiments,
}) => {
  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={true}
      useDefaultNav
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
    >
      <ShowtimesView
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        projectGuildScore={projectGuildScore}
        experiments={experiments}
      />
    </TicketsLayout>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, next404, params, res, req, query, userId }) => {
    const theatricalSlug = params?.slug
    if (!theatricalSlug || Array.isArray(theatricalSlug)) {
      return next404()
    }

    // If we have a preferred_venue_id, process it first
    if (query.preferred_venue_id && userId) {
      await setPreferredVenueFromQueryParams(query, userId, federationClient)

      // Prepare clean URL for redirect
      const { preferred_venue_id, ...cleanedQuery } = query
      const queryString = new URLSearchParams(cleanedQuery as Record<string, string>).toString()

      return {
        redirect: {
          destination: `/${locale}/tickets/${theatricalSlug}${queryString ? `?${queryString}` : ''}`,
          permanent: false,
        },
      }
    }

    const [theatricalRelease, projectGuildScore, experiments, translations] = await Promise.all([
      getTheatricalRelease({ theatricalSlug, client: federationClient }),
      getProjectGuildScore(theatricalSlug, federationClient),
      runTicketExperiments({ theatricalSlug, req, res, query, userId }),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account', 'theatrical-presales']),
    ])

    if (!theatricalRelease) {
      return next404()
    }

    return {
      props: {
        ...translations,
        theatricalSlug,
        theatricalRelease,
        locale,
        experiments: experiments ?? null,
        projectGuildScore: projectGuildScore?.guildScore,
      },
    }
  },
)

TicketsHypebotPage.getLayout = (page) => {
  return page
}

export default TicketsHypebotPage

async function runTicketExperiments({
  req,
  res,
  theatricalSlug,
  query,
  userId,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  theatricalSlug: string
  query: ParsedUrlQuery
  userId: string | null
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []
  const queryPromo = getItemOrFirstEntry(query.promo)

  if (theatricalSlug === slugs.kingOfKings) {
    expConfigs.push({ experimentKey: 'tkok-nofees-video', defaultValue: false })
    if (queryPromo === 'kidsgofree') {
      expConfigs.push({ experimentKey: 'kidsgofree-video', defaultValue: false })
    }
  }
  if (theatricalSlug === slugs.theLastRodeo) {
    expConfigs.push({ experimentKey: 'last-rodeo-group-tickets', defaultValue: false })
  }

  if (!userId) {
    expConfigs.push({ experimentKey: 'preferred_venue_selection_logged_out', defaultValue: false })
  }

  try {
    const experiments = await runExperimentsOnServer(req, res, expConfigs)
    return experiments || {}
  } catch (error) {
    logger().error('Failed to run experiments:', error)
    return expConfigs.reduce((acc, { experimentKey, defaultValue }) => {
      acc[experimentKey] = defaultValue
      return acc
    }, {} as ServerExperiments)
  }
}

const setPreferredVenueFromQueryParams = async (
  query: ParsedUrlQuery,
  userId: string | null,
  federationClient: ApolloClient<NormalizedCacheObject>,
) => {
  if (!userId) return

  if (query?.preferred_venue_id) {
    const input = {
      theaterPreferences: [
        {
          id: query.preferred_venue_id as string,
          priority: 0,
        },
      ],
    }

    try {
      await federationClient.mutate({
        mutation: UPSERT_THEATRICAL_USER_PREFERENCES,
        variables: { input },
      })
    } catch (err) {
      logger().error('Error setting preferred venue:', err)
    }
  }
}
